import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { dateTimeLabel, relativeTime } from '../../lib/format';
import { EmptyState, LoadingPage, Stat, Tag } from '../../components/ui';
import { AdminNav, DayRange, SparkBars } from './AdminNav';
import { PageHeader } from '../../components/PageHeader';

export function AppAnalyticsPage() {
  const [days, setDays] = useState(30);
  const state = useAsync(async () => api.adminAppAnalytics(days), [days]);

  if (state.loading && !state.data) return <LoadingPage label="Loading app analytics…" />;
  const data = state.data;

  return (
    <div>
      <PageHeader
        title="App analytics"
        lede="Signed-in people: who logged in, which pages they opened, and which features they used. Daily and monthly active users count community accounts, not Super Admins. Super Admin only."
      />
      <AdminNav />
      <DayRange value={days} onChange={setDays} />

      {state.error && <p className="mut">Could not load app analytics.</p>}
      {!data ? null : data.mauAllAccounts === 0 && data.logins.length === 0 ? (
        <EmptyState
          title="No app usage in this range"
          hint="Sign in as a community member to record the first session."
        />
      ) : (
        <>
          <div className="card">
            <div className="stat-grid">
              <Stat label="Daily active users" value={data.dau} />
              <Stat label="Monthly active users" value={data.mau} />
              <Stat label="DAU / MAU" value={`${Math.round(data.stickiness * 100)}%`} />
              <Stat label="DAU including Super Admin" value={data.dauAllAccounts} />
              <Stat label="MAU including Super Admin" value={data.mauAllAccounts} />
            </div>
            <SparkBars points={data.byDay.map((d) => ({ day: d.day, value: d.dau }))} label="Daily active users" />
          </div>

          {data.communities.length > 0 && (
            <div className="card">
              <h3>Communities</h3>
              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th>Community</th>
                      <th className="num">DAU</th>
                      <th className="num">MAU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.communities.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td className="num">{row.dau}</td>
                        <td className="num">{row.mau}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="split-2">
            <div className="card">
              <h3>Pages</h3>
              {data.pages.length === 0 ? (
                <p className="mut">No page views yet.</p>
              ) : (
                <div className="tw">
                  <table>
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th className="num">Views</th>
                        <th className="num">People</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pages.map((row) => (
                        <tr key={row.path + row.feature}>
                          <td>
                            {row.label}
                            <div className="mut">{row.path}</div>
                          </td>
                          <td className="num">{row.views}</td>
                          <td className="num">{row.users}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="card">
              <h3>Features</h3>
              {data.features.length === 0 ? (
                <p className="mut">No feature events yet.</p>
              ) : (
                <div className="tw">
                  <table>
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th className="num">Uses</th>
                        <th className="num">People</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.features.map((row) => (
                        <tr key={row.feature}>
                          <td>{row.label}</td>
                          <td className="num">{row.uses}</td>
                          <td className="num">{row.users}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <h3>Sign-ins</h3>
            {data.logins.length === 0 ? (
              <p className="mut">No sign-ins in this range.</p>
            ) : (
              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Person</th>
                      <th>Community</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.logins.map((row, index) => (
                      <tr key={`${row.occurredAt}-${index}`}>
                        <td>
                          {dateTimeLabel(row.occurredAt)}
                          <div className="mut">{relativeTime(row.occurredAt)}</div>
                        </td>
                        <td>
                          {row.name}
                          <div className="mut">{row.email}</div>
                          <div className="tag-row">
                            {row.roles.map((role) => (
                              <Tag key={role}>{role}</Tag>
                            ))}
                          </div>
                        </td>
                        <td>{row.communityName ?? '—'}</td>
                        <td>{row.ip ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <h3>Active people</h3>
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Community</th>
                    <th>Last seen</th>
                    <th className="num">Days active</th>
                    <th className="num">Events</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {row.name}
                        <div className="mut">{row.email}</div>
                      </td>
                      <td>{row.communityName ?? '—'}</td>
                      <td>{dateTimeLabel(row.lastSeenAt)}</td>
                      <td className="num">{row.daysActive}</td>
                      <td className="num">{row.eventCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
